/**
 * Renders saved camera bookmarks as small gizmos in the 3D scene.
 *
 * Each bookmark appears as a small wireframe frustum at its saved position,
 * oriented toward its target. Tapping a gizmo instantly switches the camera;
 * double-tapping smoothly transitions to the saved view.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import { type CameraBookmark, useStore } from "../../app/store.ts";
import { startCameraTransition } from "../cameraTransition.ts";
import { useDispose } from "../hooks/useDispose.ts";
import { DOUBLE_TAP_DIST_SQ, DOUBLE_TAP_MS } from "../interactionConstants.ts";
import { applyGizmoRenderOrder } from "./renderOrder.ts";

export function CameraBookmarkGizmos() {
  const bookmarks = useStore((s) => s.cameraBookmarks);

  if (bookmarks.length === 0) return null;

  return (
    <group userData={{ excludeFromExport: true }}>
      {bookmarks.map((bm) => (
        <BookmarkGizmo key={bm.id} bookmark={bm} />
      ))}
    </group>
  );
}

function BookmarkGizmo({ bookmark }: { bookmark: CameraBookmark }) {
  const setFocalLength = useStore((s) => s.setFocalLength);
  const groupRef = useRef<THREE.Group>(null);

  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(
    null,
  );
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { position, quaternion } = useMemo(() => {
    const pos = new THREE.Vector3(...bookmark.position);
    const tgt = new THREE.Vector3(...bookmark.target);
    const up = new THREE.Vector3(0, 0, 1);
    const mat = new THREE.Matrix4().lookAt(pos, tgt, up);
    const quat = new THREE.Quaternion().setFromRotationMatrix(mat);
    return { position: pos, quaternion: quat };
  }, [bookmark.position, bookmark.target]);

  const frustumGeo = useMemo(() => {
    const s = 0.06;
    const d = 0.15;
    const verts = new Float32Array([
      -s,
      -s,
      0,
      s,
      -s,
      0,
      s,
      s,
      0,
      -s,
      s,
      0,
      -s * 2,
      -s * 2,
      -d,
      s * 2,
      -s * 2,
      -d,
      s * 2,
      s * 2,
      -d,
      -s * 2,
      s * 2,
      -d,
    ]);

    const indices = new Uint16Array([
      0, 1, 1, 2, 2, 3, 3, 0, 4, 5, 5, 6, 6, 7, 7, 4, 0, 4, 1, 5, 2, 6, 3, 7,
    ]);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(verts, 3));
    geo.setIndex(new THREE.BufferAttribute(indices, 1));
    return geo;
  }, []);
  useDispose(frustumGeo);

  const handlePointerDown = useCallback((e: any) => {
    e.stopPropagation();
    const ne = e.nativeEvent as PointerEvent;
    pointerDownPos.current = { x: ne.clientX, y: ne.clientY };
  }, []);

  const handlePointerUp = useCallback(
    (e: any) => {
      const ne = e.nativeEvent as PointerEvent;
      const down = pointerDownPos.current;
      pointerDownPos.current = null;
      if (!down) return;
      const dx = ne.clientX - down.x;
      const dy = ne.clientY - down.y;
      if (dx * dx + dy * dy > 36) return;

      e.stopPropagation();

      const now = performance.now();
      const last = lastTapRef.current;
      const isDoubleTap =
        last != null &&
        now - last.time < DOUBLE_TAP_MS &&
        (ne.clientX - last.x) ** 2 + (ne.clientY - last.y) ** 2 <
          DOUBLE_TAP_DIST_SQ;

      if (isDoubleTap) {
        if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
        tapTimerRef.current = null;
        lastTapRef.current = null;
        startCameraTransition(
          bookmark.position,
          bookmark.target,
          bookmark.focalLength,
        );
      } else {
        lastTapRef.current = { time: now, x: ne.clientX, y: ne.clientY };
        if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
        tapTimerRef.current = setTimeout(() => {
          tapTimerRef.current = null;
          lastTapRef.current = null;
          const s = useStore.getState();
          s.setCameraOrbit(bookmark.position, bookmark.target);
          setFocalLength(bookmark.focalLength);
        }, DOUBLE_TAP_MS);
      }
    },
    [bookmark, setFocalLength],
  );

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    applyGizmoRenderOrder(group, 995);
  });

  return (
    <group
      ref={groupRef}
      position={[position.x, position.y, position.z]}
      quaternion={[quaternion.x, quaternion.y, quaternion.z, quaternion.w]}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    >
      <lineSegments geometry={frustumGeo} raycast={() => {}}>
        <lineBasicMaterial
          color="#5aA0e8"
          transparent
          opacity={0.5}
          depthTest={false}
        />
      </lineSegments>
      <mesh>
        <sphereGeometry args={[0.03, 6, 4]} />
        <meshBasicMaterial
          color="#5aA0e8"
          transparent
          opacity={0.6}
          depthTest={false}
        />
      </mesh>
    </group>
  );
}
